<template>
    <Teleport to="body">
        <Transition name="modal">
            <div v-if="isOpen" class="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
                <!-- Backdrop -->
                <div class="absolute inset-0 bg-black/70 backdrop-blur-sm" @click="$emit('close')" />

                <!-- Success State -->
                <div
                    v-if="showSuccess"
                    ref="successModalRef"
                    class="relative w-full max-w-md bg-zinc-950 rounded-t-3xl sm:rounded-2xl border-t border-white/10 sm:border border-white/10 shadow-2xl"
                    :style="successModalStyle"
                    @touchstart="handleTouchStart"
                    @touchmove="handleTouchMove"
                    @touchend="handleTouchEnd"
                >
                    <!-- Mobile Pull Handle -->
                    <div class="sm:hidden flex justify-center pt-2 pb-0.5 cursor-grab active:cursor-grabbing">
                        <div class="w-10 h-1 rounded-full bg-zinc-700" />
                    </div>

                    <!-- Close Button -->
                    <button
                        @click="closeSuccessModal"
                        class="absolute top-2.5 right-2.5 sm:top-3 sm:right-3 z-10 w-8 h-8 rounded-lg hover:bg-white/10 flex items-center justify-center transition-colors active:scale-95"
                        aria-label="Close"
                    >
                        <X class="w-4 h-4 text-white/70" />
                    </button>

                    <!-- Success Content -->
                    <div class="px-5 pt-6 pb-5 sm:px-6 sm:pt-8 sm:pb-6">
                        <!-- Success Icon & Message -->
                        <div class="flex flex-col items-center mb-6">
                            <!-- Success Circle -->
                            <div class="w-20 h-20 rounded-full bg-emerald-500/15 border-2 border-emerald-500/30 flex items-center justify-center mb-4">
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="-0.5 -0.5 16 16" fill="none" stroke-linecap="round" stroke-linejoin="round" class="w-10 h-10 stroke-emerald-400" stroke-width="1">
                                    <path d="M13.75 6.925V7.5a6.25 6.25 0 1 1 -3.70625 -5.7125"></path>
                                    <path d="M13.75 2.5 7.5 8.75625l-1.875 -1.875"></path>
                                </svg>
                            </div>

                            <!-- Success Title -->
                            <h2 class="text-emerald-400 text-2xl font-bold mb-3">
                                Stars Given!
                            </h2>

                            <!-- Success Description -->
                            <p class="text-white/70 text-center text-sm leading-relaxed mb-1">
                                You've successfully given <span class="text-blue-400 font-bold">{{ selectedAmount }} star{{ selectedAmount !== 1 ? 's' : '' }}</span> to support this audition.
                            </p>

                            <p class="text-white/50 text-center text-xs leading-relaxed">
                                Your support helps performers create great content
                            </p>
                        </div>

                        <!-- Star Animation/Visual -->
                        <div class="flex justify-center gap-1 mb-6">
                            <img
                                v-for="n in Math.min(selectedAmount, 5)"
                                :key="n"
                                :src="STAR_ICON"
                                alt=""
                                class="w-6 h-6 animate-pulse"
                                :style="{ animationDelay: `${n * 100}ms` }"
                            />
                            <span v-if="selectedAmount > 5" class="text-white/60 text-sm font-semibold self-center ml-1">
                                +{{ selectedAmount - 5 }}
                            </span>
                        </div>

                        <!-- Action Buttons -->
                        <div class="space-y-2.5">
                            <!-- Give More Stars -->
                            <button
                                @click="giveMoreStars"
                                class="w-full py-3 bg-zinc-900 hover:bg-zinc-800 border border-white/10 hover:border-white/20 text-white font-semibold rounded-xl text-sm transition-all active:scale-95"
                            >
                                Give More Stars
                            </button>

                            <!-- View Top Sponsors -->
                            <button
                                @click="viewTopSponsors"
                                :disabled="loadingTopSponsors"
                                class="w-full py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-zinc-800 disabled:text-white/30 text-white font-semibold rounded-xl text-sm transition-all active:scale-95 flex items-center justify-center"
                            >
                                <svg
                                    v-if="loadingTopSponsors"
                                    class="animate-spin h-4 w-4 mr-2 mt-3"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                >
                                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                {{ loadingTopSponsors ? 'Loading...' : viewTopSponsorsText }}
                            </button>

                            <!-- Close Text Button -->
                            <button
                                @click="closeSuccessModal"
                                class="w-full py-2 text-white/60 hover:text-white text-sm font-medium transition-colors"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Main Modal -->
                <div
                    v-else
                    ref="mainModalRef"
                    class="relative w-full max-w-md bg-zinc-950 rounded-t-3xl sm:rounded-2xl border-t border-white/10 sm:border border-white/10 shadow-2xl"
                    :style="mainModalStyle"
                    @touchstart="handleTouchStart"
                    @touchmove="handleTouchMove"
                    @touchend="handleTouchEnd"
                >
                    <!-- Mobile Pull Handle -->
                    <div class="sm:hidden flex justify-center pt-2 pb-0.5 cursor-grab active:cursor-grabbing">
                        <div class="w-10 h-1 rounded-full bg-zinc-700" />
                    </div>

                    <!-- Close Button -->
                    <button
                        @click="$emit('close')"
                        class="absolute top-2.5 right-2.5 sm:top-3 sm:right-3 z-10 w-8 h-8 rounded-lg hover:bg-white/10 flex items-center justify-center transition-colors active:scale-95"
                        aria-label="Close"
                    >
                        <X class="w-4 h-4 text-white/70" />
                    </button>

                    <!-- Content -->
                    <div class="px-5 pt-4 pb-4 sm:px-5 sm:pt-5 sm:pb-5">
                        <!-- Header -->
                        <div class="mb-3">
                            <h2 class="text-xl font-bold text-white mb-0.5 pr-8">
                                Give Stars
                            </h2>
                            <p class="text-white/60 text-sm leading-snug">
                                Your stars help decide what fantasies get released
                            </p>
                        </div>

                        <!-- Audition Info -->
                        <div class="mb-3">
                            <div class="flex items-center gap-2.5 mb-2">
                                <div class="w-12 h-12 rounded-full overflow-hidden border border-white/10 shrink-0">
                                    <img :src="audition.creator.avatar" :alt="audition.creator.name" class="w-full h-full object-cover" />
                                </div>
                                <div class="min-w-0 flex-1">
                                    <h3 class="text-white font-semibold text-xs truncate leading-tight">
                                        {{ audition.title }}
                                    </h3>
                                    <p class="text-white/60 text-sm mt-0.5">{{ audition.creator.name }}</p>
                                </div>
                            </div>

                            <!-- Progress -->
                            <div>
                                <div class="flex items-center justify-between mb-1.5">
                                        <span class="text-white font-semibold text-xs">
                                          Sponsor Goal
                                        </span>
                                    <span class="text-white text-xs font-semibold">
                                          {{ audition.likes }} / {{ audition.star_goal }}
                                        </span>
                                </div>
                                <div class="relative h-1.5 bg-white/10 rounded-full overflow-hidden mb-1">
                                    <div
                                        class="absolute inset-y-0 left-0 bg-gradient-to-r from-blue-600 to-blue-500 rounded-full transition-all duration-500"
                                        :style="{ width: `${Math.min(percentage, 100)}%` }"
                                    />
                                </div>
                                <div class="flex items-center justify-between">
                                        <span class="text-white/60 text-sm">
                                          {{ starsToGo }} to go
                                        </span>
                                    <span class="text-white/60 text-sm">
                                          {{ Math.round(percentage) }}%
                                        </span>
                                </div>
                            </div>
                        </div>

                        <!-- Balance Card -->
                        <div class="bg-zinc-900 rounded-xl px-4 py-3 border border-white/10 mb-3">
                            <div class="flex items-center justify-between">
                                <div class="flex items-center gap-1.5">
                                        <span class="text-white/70 text-xs font-semibold">
                                          Your Balance
                                        </span>
                                    <span class="text-white/40 text-[10px]">• Resets monthly</span>
                                </div>
                                <div class="flex items-center gap-2">
                                    <img :src="STAR_ICON" alt="" class="w-5 h-5" />
                                    <span class="text-white text-2xl font-bold">
                                          {{ userBalance }}
                                        </span>
                                </div>
                            </div>
                        </div>

                        <!-- Quick Select -->
                        <div class="mb-3">
                            <p class="text-white/70 text-xs mb-2 font-medium">
                                Select amount
                            </p>
                            <div class="grid grid-cols-3 gap-1.5">
                                <button
                                    v-for="amount in quickAmounts"
                                    :key="amount"
                                    @click="handleQuickSelect(amount)"
                                    :class="[
                                            'py-3 rounded-xl font-bold text-base transition-all',
                                            selectedAmount === amount && !customAmount
                                                ? 'bg-blue-600 text-white border-2 border-blue-500 scale-95'
                                                : amount > userBalance
                                                ? 'bg-zinc-900 text-white/30 border border-white/5'
                                                : 'bg-zinc-900 text-white border border-white/10 hover:border-white/20 hover:bg-zinc-800 active:scale-95'
                                        ]"
                                >
                                    <div class="flex flex-col items-center gap-0.5">
                                        <img :src="STAR_ICON" alt="" class="w-5 h-5" />
                                        <span>{{ amount }}</span>
                                    </div>
                                </button>
                            </div>
                        </div>

                        <!-- Gold Upgrade Banner -->
                        <div
                            v-if="(attemptedAmount !== null || userBalance === 0) && !isGoldUser"
                            class="mb-3 bg-amber-500/10 rounded-xl px-4 py-3 border border-amber-500/30"
                        >
                            <div class="flex items-center gap-2.5 mb-2">
                                <div class="w-6 h-6 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center flex-shrink-0">
                                    <Star class="w-3.5 h-3.5 text-black" fill="black" :stroke-width="0" />
                                </div>
                                <p class="text-amber-300 text-sm font-semibold">
                                    {{ userBalance === 0 ? 'No stars available' : 'Not enough stars' }}
                                </p>
                            </div>
                            <p class="text-white/60 text-xs mb-2.5">
                                <template v-if="attemptedAmount">
                                    You need {{ attemptedAmount - userBalance }} more stars.
                                </template>
                                Upgrade to Gold for more stars every month.
                            </p>
                            <button
                                @click="$emit('goldUpgrade')"
                                class="block w-full py-2.5 bg-amber-600 hover:bg-amber-700 active:scale-[0.98] text-white text-sm font-semibold rounded-lg transition-all shadow-lg shadow-amber-600/20 text-center"
                            >
                                Upgrade to Gold
                            </button>
                        </div>

                        <!-- Custom Amount -->
                        <div class="mb-3">
                            <label class="text-white/70 text-xs block mb-1.5">
                                Or enter custom amount
                            </label>
                            <input
                                type="number"
                                min="1"
                                :max="userBalance"
                                v-model="customAmount"
                                @input="handleCustomInput"
                                placeholder="Enter amount"
                                class="w-full px-3.5 py-2.5 bg-zinc-900 border border-white/10 rounded-xl text-white text-sm placeholder:text-white/30 focus:outline-none focus:border-blue-500 transition-colors"
                            />
                        </div>

                        <!-- Actions -->
                        <div class="grid grid-cols-2 gap-2 mb-2.5">
                            <button
                                @click="$emit('close')"
                                class="py-2.5 border border-white/15 bg-transparent text-white font-semibold rounded-xl text-xs hover:bg-white/5 transition-all active:scale-95"
                            >
                                Cancel
                            </button>
                            <button
                                @click="handleSubmit"
                                :disabled="!canSubmit || loading"
                                :class="[
                                    'h-[42px] font-bold rounded-xl text-xs transition-all flex items-center justify-center',
                                    canSubmit && !loading
                                        ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-600/25 active:scale-95'
                                        : 'bg-zinc-800 text-white/30 cursor-not-allowed'
                                ]"
                            >
                                <svg
                                    v-if="loading"
                                    class="animate-spin h-3.5 w-3.5 mr-1 mt-3"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                >
                                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                {{ loading ? 'Processing...' : submitButtonText }}
                            </button>
                        </div>

                        <!-- View Top Sponsors Link -->
                        <div class="text-center">
                            <button
                                @click="viewTopSponsors"
                                class="text-blue-400 text-xs font-semibold transition-colors inline-flex items-center justify-center gap-1.5"
                                :class="loadingTopSponsors ? 'cursor-default' : 'hover:text-blue-300'"
                            >
                                <svg
                                    v-if="loadingTopSponsors"
                                    class="animate-spin h-3.5 w-3.5 mt-3"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                >
                                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                {{ loadingTopSponsors ? 'Processing...' : viewTopSponsorsText }}
                            </button>
                        </div>

                        <p v-if="errorMessage" class="text-red-400 text-sm text-center mt-5">{{ errorMessage }}</p>
                    </div>
                </div>
            </div>
        </Transition>
    </Teleport>

    <TopSponsorsSheet
        :isOpen="showTopSponsors"
        :audition="audition"
        :sponsors="topSponsors"
        @close="showTopSponsors = false"
    />
</template>

<script setup>
import { ref, computed } from 'vue'
import { X, Star } from 'lucide-vue-next'
import TopSponsorsSheet from "@/Components/Reels/TopSponsorsSheet.vue";
import {trackAuditionAnalyticsEvent} from "@/Helpers/auditions";

const STAR_ICON = 'https://sm.naughtycdn.com/images/landers/prize/NA-Star.png'

const props = defineProps({
    isOpen: {
        type: Boolean,
        required: true
    },
    audition: {
        type: Object,
        default: null
    },
    userBalance: {
        type: Number,
        required: true
    },
    isGoldUser: {
        type: Boolean,
        required: true
    },
    loading: {
        type: Boolean,
    },
    errorMessage: {
        type: String,
        default: ''
    },
    showSuccess: {
        type: Boolean,
        default: false
    }
})

const emit = defineEmits(['close', 'submit', 'resetSuccess', 'goldUpgrade'])

const selectedAmount = ref(null)
const customAmount = ref('')
const showTopSponsors = ref(false)
const attemptedAmount = ref(null)
const topSponsors = ref([])
const loadingTopSponsors = ref(false)

const quickAmounts = [1, 5, 10, 25, 50, 100]

// Touch/swipe handling
const mainModalRef = ref(null)
const successModalRef = ref(null)
const touchStartY = ref(0)
const touchCurrentY = ref(0)
const isDragging = ref(false)

const mainModalStyle = computed(() => {
    if (!isDragging.value || props.showSuccess) return {}

    const deltaY = touchCurrentY.value - touchStartY.value
    if (deltaY < 0) return {} // Don't allow swipe up

    return {
        transform: `translateY(${deltaY}px)`,
        transition: 'none'
    }
})

const successModalStyle = computed(() => {
    if (!isDragging.value || !props.showSuccess) return {}

    const deltaY = touchCurrentY.value - touchStartY.value
    if (deltaY < 0) return {} // Don't allow swipe up

    return {
        transform: `translateY(${deltaY}px)`,
        transition: 'none'
    }
})

const handleTouchStart = (e) => {
    const modalRef = props.showSuccess ? successModalRef.value : mainModalRef.value
    if (!modalRef) return

    // Only handle touches on the handle area (top 60px)
    const touch = e.touches[0]
    const rect = modalRef.getBoundingClientRect()

    if (touch.clientY - rect.top > 60) return // Not in handle area

    touchStartY.value = touch.clientY
    touchCurrentY.value = touch.clientY
    isDragging.value = true
}

const handleTouchMove = (e) => {
    if (!isDragging.value) return

    const touch = e.touches[0]
    touchCurrentY.value = touch.clientY

    const deltaY = touchCurrentY.value - touchStartY.value

    // Prevent scrolling when dragging down
    if (deltaY > 0) {
        e.preventDefault()
    }
}

const handleTouchEnd = () => {
    if (!isDragging.value) return

    const deltaY = touchCurrentY.value - touchStartY.value
    const threshold = 100 // Close if dragged more than 100px

    if (deltaY > threshold) {
        if (props.showSuccess) {
            closeSuccessModal()
        } else {
            emit('close')
        }
    }

    isDragging.value = false
    touchStartY.value = 0
    touchCurrentY.value = 0
}

const percentage = computed(() => (props.audition.likes / props.audition.star_goal * 100))
const starsToGo = computed(() => props.audition.star_goal - props.audition.likes < 0 ? 0 : props.audition.star_goal - props.audition.likes)
const canSubmit = computed(() => {
    return selectedAmount.value && selectedAmount.value > 0 && selectedAmount.value <= props.userBalance
})
const submitButtonText = computed(() => {
    const amount = selectedAmount.value || parseInt(customAmount.value) || 0
    return `Give ${amount} Star${amount !== 1 ? 's' : ''}`
})
const viewTopSponsorsText = computed(() => {
    return props.audition.unique_sponsors_count > 0 ?
        `View Sponsors (${props.audition.unique_sponsors_count})` :
        'View Sponsors'
})

const handleQuickSelect = (amount) => {
    if (amount > props.userBalance) {
        // User clicked disabled button - show banner
        attemptedAmount.value = amount

        if (!props.isGoldUser) {
            trackAuditionAnalyticsEvent('Not Enough Stars Alert Triggered', props.audition);
        }

        return
    }
    selectedAmount.value = amount
    customAmount.value = ''
    attemptedAmount.value = null
}

const handleCustomInput = () => {
    const numValue = parseInt(customAmount.value) || 0

    if (numValue > props.userBalance) {
        attemptedAmount.value = numValue
        selectedAmount.value = null

        if (!props.isGoldUser) {
            trackAuditionAnalyticsEvent('Not Enough Stars Alert Triggered', props.audition);
        }
    } else {
        selectedAmount.value = numValue > 0 ? numValue : null
        attemptedAmount.value = null
    }
}

const handleSubmit = () => {
    if (canSubmit.value) {
        emit('submit', selectedAmount.value)
    }
}

const viewTopSponsors = () => {
    loadingTopSponsors.value = true

    axios
        .get(route('members2.reels.top-sponsors', {
            post: props.audition.id,
            page: 1,
            per_page: 10,
        }))
        .then(function (response) {
            topSponsors.value = response.data
            loadingTopSponsors.value = false
            showTopSponsors.value = true
        })
        .catch(error => {
            console.error(error)
            loadingTopSponsors.value = false
        })
}

const giveMoreStars = () => {
    emit('resetSuccess')
    // Reset the modal to allow giving more stars
    setTimeout(() => {
        selectedAmount.value = null
        customAmount.value = ''
        attemptedAmount.value = null
    }, 300)
}

const closeSuccessModal = () => {
    emit('resetSuccess')
    emit('close')
}
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
    transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
    opacity: 0;
}

@keyframes pulse {
    0%, 100% {
        opacity: 1;
        transform: scale(1);
    }
    50% {
        opacity: 0.7;
        transform: scale(1.05);
    }
}

.animate-pulse {
    animation: pulse 1.5s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}
</style>
